<div class="job-preview-content">
  <!-- Fallback: Show raw text if parsing didn't extract content -->
  <div v-if="!has_parsed_content && raw_text" class="mb-4">
    <div class="alert alert-warning border-0 rounded-3 text-xxs mb-3">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Unable to parse structured sections. Showing raw document text below.
    </div>
    <div class="card border-0 bg-light rounded-3 p-3">
      <pre class="mb-0 text-xxs text-break">{{ raw_text }}</pre>
    </div>
  </div>

  <!-- Parsed Header -->
  <div v-if="parsed_document && has_parsed_content" class="mb-4">
    <h6 class="text-xs fw-bold text-primary mb-2">{{ parsed_document.meta?.post_title || 'Job Title' }}</h6>
    <small class="text-muted text-xxs">{{ parsed_document.meta?.location }}</small>
  </div>

  <!-- Section A: Job Information -->
  <div v-if="parsed_active_section === 'meta' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">A. Job Information Summary</h6>
    <div class="row g-2">
      <div class="col-md-6" v-for="[key, value] in filtered_meta_fields" :key="key">
        <div class="card border-0 bg-light p-2 h-100 rounded-3">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">{{ format_label(key) }}</small>
          <span class="text-xxs fw-medium">{{ value }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Section B: Job Purpose -->
  <div v-else-if="parsed_active_section === 'job_purpose' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">B. Job Purpose</h6>
    <div class="card border-0 bg-light p-3 rounded-3">
      <p class="mb-0 text-xxs lh-lg">{{ parsed_document?.job_purpose || 'No job purpose defined.' }}</p>
    </div>
  </div>

  <!-- Section C: Main Functions -->
  <div v-else-if="parsed_active_section === 'main_functions' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">C. Main Functions of the Post</h6>
    <ul class="list-unstyled mb-0">
      <li v-for="(func, idx) in parsed_document?.main_functions" :key="idx" class="mb-2 d-flex align-items-start">
        <span class="badge bg-primary me-2 text-xxs">{{ idx + 1 }}</span>
        <span class="text-xxs flex-grow-1">{{ func }}</span>
      </li>
      <li v-if="!parsed_document?.main_functions?.length" class="text-muted text-xxs">No main functions defined.</li>
    </ul>
  </div>

  <!-- Section D: Key Responsibilities -->
  <div v-else-if="parsed_active_section === 'responsibilities' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">D. Main Objectives / Key Responsibilities</h6>
    <div v-if="parsed_document?.responsibilities?.length">
      <div v-for="(resp, idx) in parsed_document.responsibilities" :key="idx" class="mb-3 card border-0 bg-light rounded-3 p-3">
        <h6 class="text-xs fw-bold mb-2 text-dark">{{ resp.heading }}</h6>
        <ul class="list-unstyled mb-0 ps-2">
          <li v-for="(activity, aIdx) in resp.activities" :key="aIdx" class="mb-1 d-flex align-items-start">
            <i class="bi bi-dot text-primary me-1"></i>
            <span class="text-xxs">{{ activity }}</span>
          </li>
        </ul>
      </div>
    </div>
    <p v-else class="text-muted text-xxs">No responsibilities defined.</p>
  </div>

  <!-- Section E: Outputs Profile -->
  <div v-else-if="parsed_active_section === 'outputs_profile' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">E. Outputs Profile (Stakeholder Expectations)</h6>
    <div v-if="parsed_document?.outputs_profile?.length">
      <div class="table-responsive">
        <table class="table table-hover text-xxs">
          <thead class="custom-header">
            <tr>
              <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Customer</th>
              <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-start">Expectations / Deliverables</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(output, idx) in parsed_document.outputs_profile" :key="idx">
              <td class="text-xxs fw-bold align-top">{{ output.customer }}</td>
              <td class="text-xxs">
                <ul class="list-unstyled mb-0">
                  <li v-for="(exp, eIdx) in output.expectations" :key="eIdx" class="mb-1">
                    <i class="bi bi-dot text-primary"></i> {{ exp }}
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <p v-else class="text-muted text-xxs">No outputs profile defined.</p>
  </div>

  <!-- Section F: Core Competencies -->
  <div v-else-if="parsed_active_section === 'competencies' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">F. Core Competencies</h6>
    <div v-if="parsed_document?.competencies" class="row g-2">
      <div class="col-md-6">
        <div class="card border-0 bg-light p-2 rounded-3 h-100">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">Education</small>
          <span class="text-xxs">{{ parsed_document.competencies.minimum_qualification || 'Not specified' }}</span>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card border-0 bg-light p-2 rounded-3 h-100">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">Minimum Experience</small>
          <span class="text-xxs">{{ parsed_document.competencies.minimum_experience || 'Not specified' }}</span>
        </div>
      </div>
      <div class="col-md-6" v-if="parsed_document.competencies.knowledge?.length">
        <div class="card border-0 bg-light p-2 rounded-3 h-100">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">Knowledge</small>
          <ul class="list-unstyled mb-0">
            <li v-for="(item, idx) in parsed_document.competencies.knowledge" :key="idx" class="text-xxs">
              <i class="bi bi-dot text-primary"></i> {{ item }}
            </li>
          </ul>
        </div>
      </div>
      <div class="col-md-6" v-if="parsed_document.competencies.skills?.length">
        <div class="card border-0 bg-light p-2 rounded-3 h-100">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">Skills</small>
          <ul class="list-unstyled mb-0">
            <li v-for="(item, idx) in parsed_document.competencies.skills" :key="idx" class="text-xxs">
              <i class="bi bi-dot text-primary"></i> {{ item }}
            </li>
          </ul>
        </div>
      </div>
      <div class="col-12" v-if="parsed_document.competencies.personal_attributes?.length">
        <div class="card border-0 bg-light p-2 rounded-3">
          <small class="text-muted d-block mb-1 text-uppercase text-xxs">Personal Attributes</small>
          <span class="text-xxs">{{ parsed_document.competencies.personal_attributes.join(', ') }}</span>
        </div>
      </div>
    </div>
    <p v-else class="text-muted text-xxs">No competencies defined.</p>
  </div>

  <!-- Section G: Career Pathing -->
  <div v-else-if="parsed_active_section === 'career_pathing' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">G. Career Pathing</h6>
    <div class="card border-0 bg-light p-3 rounded-3">
      <p class="mb-0 text-xxs">{{ parsed_document?.career_pathing || 'No career pathing information available.' }}</p>
    </div>
  </div>

  <!-- Section H: Amendments -->
  <div v-else-if="parsed_active_section === 'amendments' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">H. Amendments to Job Description</h6>
    <div class="card border-0 bg-light p-3 rounded-3">
      <p class="mb-0 text-xxs">{{ parsed_document?.amendments || 'No amendments recorded.' }}</p>
    </div>
  </div>

  <!-- Section I: Performance Agreement -->
  <div v-else-if="parsed_active_section === 'performance_agreement' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">I. Performance Agreement</h6>
    <div class="card border-0 bg-light p-3 rounded-3">
      <p class="mb-0 text-xxs">{{ parsed_document?.performance_agreement || 'No performance agreement details.' }}</p>
    </div>
  </div>

  <!-- Section J: Signatories -->
  <div v-else-if="parsed_active_section === 'signatories' && has_parsed_content">
    <h6 class="fw-bold mb-3 text-xs text-primary border-bottom pb-2">J. Job Description Agreement</h6>
    <div v-if="parsed_document?.signatories?.length" class="row g-2">
      <div v-for="(sig, idx) in parsed_document.signatories" :key="idx" class="col-md-4">
        <div class="card border-0 bg-light p-3 rounded-3 h-100 text-center">
          <small class="text-muted d-block mb-2 text-uppercase text-xxs fw-bold">{{ sig.role }}</small>
          <div class="border-bottom mb-2 py-3">
            <span class="text-xxs">{{ sig.name || '________________' }}</span>
          </div>
          <small class="text-muted text-xxs">Signature & Date</small>
        </div>
      </div>
    </div>
    <p v-else class="text-muted text-xxs">No signatories defined.</p>
  </div>

  <!-- Fallback for unknown sections -->
  <div v-else>
    <div class="alert alert-info border-0 rounded-3 text-xxs">
      <i class="bi bi-info-circle me-2"></i>
      Select a section from the sidebar to view its content.
    </div>
  </div>
</div>
